import type { RequestStatus } from '@features/request/requestSlice';
import type { ServiceResult } from '@gql/graphql';
import { useRequest } from 'features/request/useRequest';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimcRemoveCalculationLimitedCostsGql } from '../bimCalculationLimitedQueries';

interface IUseBimDeleteCalculationLimitedCost {
    calculationLimitedCostId?: number;
    calculationId: number;
}

export const useBimDeleteCalculationLimitedCost = (
    props: IUseBimDeleteCalculationLimitedCost,
): {
    data: ServiceResult | undefined;
    reset: () => void;
    error: any;
    makeRequest: () => void;
    status: RequestStatus;
} => {
    const { calculationLimitedCostId, calculationId } = props;
    const dispatch = useDispatch();

    const variables = useMemo(
        () => ({ calculationId, ids: [calculationLimitedCostId as number] }),
        [calculationId, calculationLimitedCostId],
    );

    const [
        bimDeleteCalculationLimitedCostData,
        bimDeleteCalculationLimitedCostStatus,
        bimDeleteCalculationLimitedCostError,
        bimDeleteCalculationLimitedCostMakeRequest,
        bimDeleteCalculationLimitedCostSetData,
    ] = useRequest({
        requestId: `bimcDeleteCalculationLimitedCost${calculationLimitedCostId}`,
        query: bimcRemoveCalculationLimitedCostsGql,
        withEffect: false,
    });

    const makeRequest = useCallback(() => {
        dispatch(bimDeleteCalculationLimitedCostMakeRequest(variables));
    }, [dispatch, bimDeleteCalculationLimitedCostMakeRequest, variables]);

    const reset = useCallback(() => {
        dispatch(bimDeleteCalculationLimitedCostSetData(undefined));
    }, [bimDeleteCalculationLimitedCostSetData, dispatch]);

    return {
        makeRequest,
        reset,
        data: bimDeleteCalculationLimitedCostData?.bimcRemoveCalculationLimitedCosts as ServiceResult | undefined,
        status: bimDeleteCalculationLimitedCostStatus,
        error: bimDeleteCalculationLimitedCostError,
    };
};
